import { isValidPhoneNumber } from "libphonenumber-js";

// ==================== Password ====================
// Single source of truth for password complexity — every path that sets or
// changes a password (client/talent/content-creator registration, admin
// user-create, change-password) enforces the same rule.

export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[a-zA-Z\d@$!%*?&]{8,}$/;

export const PASSWORD_REQUIREMENTS_MESSAGE =
  "Password must contain uppercase, lowercase, number, and special character";

// Translation-key twin of PASSWORD_REQUIREMENTS_MESSAGE — used by the paths
// that already run their result through t() (auth.schema.ts's registerBasic/
// changePassword schemas, register-talent.utils.ts, useRegisterContentCreator)
// so the same rule can be localized instead of showing raw English. Left as a
// separate export rather than changing PASSWORD_REQUIREMENTS_MESSAGE itself,
// since that one is still consumed verbatim (untranslated) elsewhere (e.g.
// admin's useCreateUser.ts) and changing its value would leak a bare key there.
export const PASSWORD_REQUIREMENTS_KEY = "auth.validation.passwordComplexity";

export const isValidPassword = (value: string): boolean =>
  PASSWORD_REGEX.test(value);

// ==================== Email ====================

export const isLikelyEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// ==================== Phone ====================

export const isValidPhone = (value: string | undefined | null): boolean =>
  !!value && isValidPhoneNumber(value);

// ==================== Age ====================

export const calculateAge = (dateString: string): number | null => {
  const birth = new Date(dateString);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : null;
};

// ==================== Field-level "already exists" errors ====================
// Shared by every registration step that runs a live duplicate-check
// (usePhoneCheck/useEmailCheck) alongside its own client-side field error.

export const getExistsFieldError = (
  fieldError: string | undefined,
  checking: boolean | undefined,
  exists: boolean | undefined,
  existsMessage: string,
): string | undefined =>
  fieldError || (checking ? undefined : exists ? existsMessage : undefined);
